<template>
    <div class="vld-parent" style="min-height: 600px">

        <loading 
            :active="loading" 
            :can-cancel="false" 
            :is-full-page="false"
        />

        <div class="row">
            <div class="col-lg-4 m-b-20">
                <div>{{$t("task_details:manage:enter_re_key_with_new_lines")}}</div>
                <add-rekeys-textarea
                    @submit="addRealEstates"
                />
            </div>
        </div>

        <div class="row" v-if="orderedRealEstates.length > 0">
            <div class="col-md-12">
                <table class="table table-responsive">
                    <thead>
                        <tr>
                            <th>{{$t("general:re_key")}}</th>
                            <th class="text-nowrap">{{$t("task_details:manage:leading")}} <help-icon slug="predmet-vrednotenja-vodilna-nepremicnina" /></th>
                            <th>{{$t("general:real_estate")}}</th>
                            <th>{{$t("task_details:manage:considered_re_type")}}</th>
                            <th>{{$t("task_details:manage:considered_size")}}</th>
                            <th class="text-center">{{$t("task_details:manage:owners")}}</th>
                        </tr>
                    </thead>

                    <tbody>
                        <tr 
                            v-for="(item, idx) in orderedRealEstates"
                            :key="'valuation-subject-item-'+idx"
                            :class="isRowHighlighted(item) ? 'warning' : ''"
                            @mouseover="highlightRealEstate(item.re_key)"
                            @mouseleave="highlightRealEstate(null)"
                        >
                            <!-- ID znak -->
                            <td class="text-nowrap">
                                <re-open-modal :re_key="item.re_key" />
                                <div>
                                    <span 
                                        class="text-danger small custom-cursor-pointer" 
                                        v-tooltip="$t('task_details:manage:remove_from_validation')"
                                        @click.prevent="removeRealEstate(item.id)"
                                    >
                                        {{$t("general:remove")}}
                                    </span>
                                </div>
                            </td>

                            <!-- Vodilna -->
                            <td>
                                <input type="checkbox" 
                                    :checked="item.main_property"
                                    @click="handlesetMainPropertyCheckboxClick($event, item)"
                                />
                            </td>

                            <!-- Nepremičnina -->
                            <td>
                                <div v-if="item.real_estate">
                                    <span>
                                        {{item.real_estate.summary}}
                                        <i
                                            v-if="_hasSeparateBasementsOrGarages(item.real_estate)"
                                            v-tooltip="$t('task_details:manage:building_has_seperate_re_keys_for_cellar_or_parking')"
                                            class="fa fa-building text-warning m-l-5"
                                        ></i>
                                    
                                    </span>

                                    <real-estate-additional-info
                                        :real_estate="item.real_estate"
                                        :subject="subject"
                                        :session="session"
                                        @add-real-estates="addRealEstates"
                                    />
                                </div>                        
                            </td>

                            <!-- Tip -->
                            <td>
                                <type-dropwdown
                                    v-model="item.custom_type"
                                    :default-value="item.real_estate.re_type"
                                    :isParcel="item.real_estate.is_parcel"
                                    @reset="item.custom_type=null"
                                />
                            </td>

                            <!-- Velikost -->
                            <td>
                                <div>
                                    <div style="float:left; width:90%" >
                                        <number-input-field
                                            v-model="item.custom_size"
                                            v-on:input="$emit('size-changed')"
                                            :placeholder="_getRealEstateSizeDisplay(item)"
                                            inputFieldsAddon="m2"
                                            size="sm"
                                        />
                                    </div>
                                    <div style="float:right; width:10%">
                                        <unit-size-estimate-indicator
                                            v-if="item.real_estate.unit && !item.custom_size"
                                            :unit="item.real_estate.unit" 
                                        >
                                        </unit-size-estimate-indicator>
                                    </div>
                                </div>
                            </td>

                            <!-- Lastniki -->
                            <td class="text-center">
                                <legal-status-display
                                    :real-estate="item.real_estate"
                                    :re-key="item.re_key"
                                    @get-legal-status="$emit('get-legal-status', $event)"
                                />
                            </td>
                        </tr>

                    </tbody>
                </table>
            </div>
        </div>
        
        <!-- Functional Whole Section -->
        <div class="row m-t-20" v-if="enableFunctionalWhole && functionalWholeData">
            <div class="col-md-12">
                <h4>{{$t("functional_whole:functional_whole")}}</h4>
                <!-- Functional Whole Results Table -->
                <div v-if="filteredFunctionalWholeProperties.length > 0">                            
                    <table class="table table-responsive">
                        <thead>
                            <tr>
                                <th>{{$t("general:re_key")}}</th>
                                <th>{{$t("general:real_estate")}}</th>
                                <th>{{$t("task_details:manage:considered_re_type")}}</th>
                                <th>{{$t("task_details:manage:considered_size")}}</th>
                                <th class="text-center">{{$t("task_details:manage:owners")}}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr 
                                v-for="property in filteredFunctionalWholeProperties" 
                                :key="'functional-whole-'+property.re_key"
                                @mouseover="highlightRealEstate(property.re_key)"
                                @mouseleave="highlightRealEstate(null)"
                            >
                                <td class="text-nowrap">
                                    <re-open-modal :re_key="property.re_key" />

                                    <add-remove-button
                                        :re_key="property.re_key"
                                        :added="false"
                                        @add-real-estates="addFunctionalWholePropertyToSubject(property.re_key)"
                                    />
                                </td>
                                <td>
                                    <div v-if="functionalWholeRealEstates[property.re_key]">
                                        <span>
                                            {{functionalWholeRealEstates[property.re_key].summary}}
                                            <i
                                                v-if="_hasSeparateBasementsOrGarages(functionalWholeRealEstates[property.re_key])"
                                                v-tooltip="$t('task_details:manage:building_has_seperate_re_keys_for_cellar_or_parking')"
                                                class="fa fa-building text-warning m-l-5"
                                            ></i>
                                        </span>
                                    </div>
                                    <div v-else-if="functionalWholeLoadingData[property.re_key]">
                                        <i class="fa fa-spinner fa-spin"></i>
                                    </div>
                                    <div v-else>
                                        <span class="text-muted">{{property.re_key}}</span>
                                    </div>
                                </td>
                                <td>
                                    <div v-if="functionalWholeRealEstates[property.re_key]">
                                        <type-dropwdown
                                            :value="null"
                                            :default-value="functionalWholeRealEstates[property.re_key].re_type"
                                            :isParcel="functionalWholeRealEstates[property.re_key].is_parcel"
                                            :disabled="true"
                                        />
                                    </div>
                                    <div v-else>
                                        <span class="text-muted">-</span>
                                    </div>
                                </td>
                                <td>
                                    <div v-if="functionalWholeRealEstates[property.re_key]">
                                        <div>
                                            <div style="float:left; width:90%" >
                                                <number-input-field
                                                    :value="null"
                                                    :placeholder="_getFunctionalWholeRealEstateSizeDisplay(functionalWholeRealEstates[property.re_key])"
                                                    inputFieldsAddon="m2"
                                                    size="sm"
                                                    :disabled="true"
                                                />
                                            </div>
                                            <div style="float:right; width:10%">
                                                <unit-size-estimate-indicator
                                                    v-if="functionalWholeRealEstates[property.re_key].unit && !functionalWholeRealEstates[property.re_key].unit.size"
                                                    :unit="functionalWholeRealEstates[property.re_key].unit" 
                                                >
                                                </unit-size-estimate-indicator>
                                            </div>
                                        </div>
                                    </div>
                                    <div v-else>
                                        <span class="text-muted">-</span>
                                    </div>
                                </td>
                                <td class="text-center">
                                    <legal-status-display
                                        :real-estate="functionalWholeRealEstates[property.re_key]"
                                        :re-key="property.re_key"
                                        @get-legal-status="getFunctionalWholeLegalStatus"
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                
                <div v-else-if="functionalWholeData && (functionalWholeData.parcels_same_owner.length > 0 || functionalWholeData.units_same_owner.length > 0)">
                    <div>
                        {{$t("functional_whole:all_properties_already_in_subject")}}
                    </div>
                </div>
            </div>
        </div>

        <div class="row m-b-10" v-if="orderedRealEstates.length > 0">
            <div class="col-lg-12 text-center">
                <button
                    class="btn btn-info"
                    v-on:click.prevent="$emit('close-me')"
                >
                    {{$t("general:save")}}
                </button>

                <button
                    v-if="enableFunctionalWhole"
                    class="btn btn-outline btn-info m-l-10"
                    @click.prevent="analyzeFunctionalWhole"
                    :disabled="functionalWholeLoading"
                >
                    {{$t("functional_whole:analyze")}}
                    <i v-if="functionalWholeLoading" class="fa fa-spinner fa-spin"></i>
                </button>
            </div>
        </div>

        <div class="row" v-if="orderedRealEstates.length > 0">
            <div class="col-lg-12">
                <valuation-subject-wizard-map
                    :real_estates="orderedRealEstates"
                    :highlighted_re_key="highlighted_re_key"
                    @add-real-estates="addRealEstates"
                    @highlight-real-estate="highlightRealEstate"
                />
            </div>
        </div>
    </div>
</template>

<script>
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";

import ReOpenModal from "@/components/widgets/ReOpenModal.vue"
import HelpIcon from "@/components/widgets/HelpIcon";
import NumberInputField from "@/components/utils/inputs/NumberInputField.vue";
import ApiCalls from "@/components/utils/api.calls.js";

import AddRemoveButton from "../components/map/components/AddRemoveButton.vue";
import AddRekeysTextarea from "../components/AddRekeysTextarea.vue"
import RealEstateAdditionalInfo from "../components/RealEstateAdditionalInfo.vue"
import ValuationSubjectWizardMap from "../components/map/ValuationSubjectWizardMap.vue"
import RealEstateTypeDropDown from "../components/RealEstateTypeDropDown.vue";
import LegalStatusDisplay from "../components/LegalStatusDisplay.vue";
import VSUtils from "../components/utils.js"

import UnitSizeEstimateIndicator from "@apps/real_estate/vue/components/UnitSizeEstimateIndicator.vue"

export default {
    model: {
        prop: "subject",
        event: "change"
    },
    props: {
        subject: {
            type: Object,
            default: () => {}
        },
        session: {
            type: Object,
            required: true
        },
        loading: {
            type: Boolean,
            default: false
        },
        enableFunctionalWhole: {
            type: Boolean,
            default: false
        }
    },
    components: {
        Loading, HelpIcon,
        ReOpenModal,
        AddRemoveButton,
        AddRekeysTextarea,
        RealEstateAdditionalInfo,
        ValuationSubjectWizardMap,
        NumberInputField,
        "type-dropwdown": RealEstateTypeDropDown,
        UnitSizeEstimateIndicator,
        LegalStatusDisplay
    },
    data: function () {
        return {
            VSUtils: VSUtils,
            highlighted_re_key: null,
            functionalWholeData: null,
            functionalWholeLoading: false,
            functionalWholeRealEstates: {},
            functionalWholeLoadingData: {},
        }
    },
    methods: {
        addRealEstates: function(re_keys) {
            if(!Array.isArray(re_keys)) {
                re_keys = re_keys.trim().split(/,|\n/)
                re_keys = re_keys.map((re_key) => re_key.trim())
            }
            this.$emit("add-re-to-subject", re_keys, this.session.id);
        },
        removeRealEstate: function(re_id) {
            this.$emit("remove-re-from-subject", re_id);
        },
        highlightRealEstate: function(re_key) {
            this.highlighted_re_key = re_key;
        },

        _getRealEstateSizeDisplay: function(real_estate){
            return VSUtils.getRealEstateSize(real_estate);
        },
        _getFunctionalWholeRealEstateSizeDisplay: function(realEstateData) {
            // For functional whole, the data structure is different - it's the real estate data directly
            if (!realEstateData) return "";
            
            // Create a wrapper object that matches the expected structure
            const wrapper = {
                custom_size: null,
                real_estate: realEstateData
            };
            
            return VSUtils.getRealEstateSize(wrapper);
        },
        _hasSeparateBasementsOrGarages: function(real_estate){
            return real_estate.unit && real_estate.unit.building && (real_estate.unit.building.basements_separate_ids || real_estate.unit.building.garage_or_parking_separate_ids);
        },
        isRowHighlighted: function(real_estate) {
            // Handle building re_keys and apply correct class for units.
            let simple_re_key = real_estate.re_key.split("-")[0] + "-" + real_estate.re_key.split("-")[1];
            return real_estate.re_key===this.highlighted_re_key || simple_re_key===this.highlighted_re_key;
        },
        handlesetMainPropertyCheckboxClick: function(e, real_estate){
            if(e.target.checked){
                this.$emit("set-main-property", real_estate);
            } else {
                e.preventDefault();
            }
        },

        // Functional Whole Methods
        hasParcelInSession() {
            const realEstates = VSUtils.getSessionRealEstates(this.subject.real_estates, this.session);
            return realEstates.some(re => re.real_estate && re.real_estate.is_parcel) || 
                   realEstates.some(re => re.real_estate && re.real_estate.unit && re.real_estate.unit.building);
        },
        
        async analyzeFunctionalWhole() {
            const realEstates = VSUtils.getSessionRealEstates(this.subject.real_estates, this.session);
            if (realEstates.length === 0) {
                this.$notify({
                    group: 'warn',
                    type: 'warn',
                    title: this.$t("general:warning"),
                    text: this.$t("functional_whole:not_found"),
                });
                return;
            }
            
            // Get all re_keys from the session
            const reKeys = realEstates.map(re => re.re_key);
            
            this.functionalWholeLoading = true;
            
            try {
                const data = await ApiCalls.getFunctionalWhole(...reKeys);
                if (data) {
                    this.functionalWholeData = data;
                    
                    // Fetch real estate data for all properties in functional whole
                    await this.fetchFunctionalWholeRealEstateData();
                }
            } catch (error) {
                this.$notify({
                    group: 'warn',
                    type: 'warn',
                    title: this.$t("general:error"),
                    text: this.$t("functional_whole:analysis_failed"),
                });
            } finally {
                this.functionalWholeLoading = false;
            }
        },
        
        async fetchFunctionalWholeRealEstateData() {
            if (!this.functionalWholeData) return;
            
            // Get all re_keys from functional whole results
            const allReKeys = [
                ...this.functionalWholeData.parcels_same_owner,
                ...this.functionalWholeData.units_same_owner
            ];
            
            // Initialize loading state for each re_key
            allReKeys.forEach(re_key => {
                this.$set(this.functionalWholeLoadingData, re_key, true);
            });
            
            // Fetch real estate data for each re_key
            const promises = allReKeys.map(async (re_key) => {
                try {
                    const realEstateData = await ApiCalls.getRealEstateData(re_key);
                    if (realEstateData) {
                        this.$set(this.functionalWholeRealEstates, re_key, realEstateData);
                    }
                } catch (error) {
                    console.error(`Failed to fetch real estate data for ${re_key}:`, error);
                } finally {
                    this.$set(this.functionalWholeLoadingData, re_key, false);
                }
            });
            
            await Promise.all(promises);
        },
        getFunctionalWholeLegalStatus(re_key) {
            if (this.functionalWholeRealEstates[re_key]) {
                this.functionalWholeRealEstates[re_key].legal_status = "loading";
                ApiCalls.getLegalStatus(re_key)
                    .then(res => {
                        this.$set(this.functionalWholeRealEstates[re_key], 'legal_status', res);
                    })
                    .catch(error => {
                        this.$set(this.functionalWholeRealEstates[re_key], 'legal_status', null);
                        this.$notify({
                            group: 'warn',
                            type: 'warn',
                            title: this.$t("general:warning"),
                            text: (error.response && error.response.data && error.response.data.message) || this.$t("general:error"),
                        });
                    });
            }
        },
        addFunctionalWholePropertyToSubject(re_key) {
            this.$emit("add-re-to-subject", [re_key], this.session.id);
        }
    },
    computed: {
        orderedRealEstates: function() {
            return VSUtils.getSessionRealEstates(this.subject.real_estates, this.session);
        },
        filteredFunctionalWholeProperties() {
            if (!this.functionalWholeData) return [];
            const properties = [
                ...this.functionalWholeData.parcels_same_owner.map(re_key => ({ re_key })),
                ...this.functionalWholeData.units_same_owner.map(re_key => ({ re_key }))
            ];
            return properties.filter(property => !this.subject.real_estates.some(re => re.re_key === property.re_key));
        }
    }
}
</script>
